/**
 * 地圖錯誤回報（前端）
 * POST /api/map-reports — 需登入；不直接改圖資
 */

const LAYERS = [
    { value: 'rail_station', label: '軌道車站' },
    { value: 'rail_route', label: '軌道路線' },
    { value: 'rail_exit', label: '軌道出口' },
    { value: 'other', label: '其他' }
];

const CATEGORIES = [
    { value: 'elevation', label: '高程（地下／高架／平面）' },
    { value: 'geometry', label: '位置／形狀' },
    { value: 'name', label: '名稱' },
    { value: 'missing', label: '缺漏' },
    { value: 'other', label: '其他' }
];

const ELEVATIONS = [
    { value: '', label: '（不指定）' },
    { value: 'underground', label: '地下' },
    { value: 'elevated', label: '高架' },
    { value: 'at_grade', label: '平面' },
    { value: 'unknown', label: '未知' },
    { value: 'other', label: '其他' }
];

function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function mapContext(map) {
    const c = map?.getCenter?.();
    return {
        host: typeof location !== 'undefined' ? location.host : '',
        path: typeof location !== 'undefined' ? location.pathname + location.search : '',
        map_center: c ? { lat: c.lat, lng: c.lng } : null,
        zoom: map?.getZoom?.() ?? null
    };
}

/**
 * @param {object} opts
 * @param {import('leaflet').Map} [opts.map]
 * @param {string} opts.layer
 * @param {string} [opts.feature_std_id]
 * @param {string} [opts.sys]
 * @param {string} [opts.name]
 * @param {string} [opts.current_elevation]
 * @param {string} [opts.appVersion]
 */
export function openMapErrorReportForm(opts = {}) {
    const existing = document.getElementById('map-error-report-modal');
    if (existing) existing.remove();

    const ctx = mapContext(opts.map);
    const overlay = document.createElement('div');
    overlay.id = 'map-error-report-modal';
    overlay.style.cssText =
        'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px;';

    const layerOpts = LAYERS.map(
        (l) =>
            `<option value="${l.value}"${l.value === (opts.layer || 'rail_station') ? ' selected' : ''}>${esc(l.label)}</option>`
    ).join('');
    const catOpts = CATEGORIES.map(
        (c) => `<option value="${c.value}"${c.value === 'elevation' ? ' selected' : ''}>${esc(c.label)}</option>`
    ).join('');
    const elevOpts = ELEVATIONS.map((e) => `<option value="${e.value}">${esc(e.label)}</option>`).join('');

    overlay.innerHTML = `
      <div role="dialog" aria-labelledby="mer-title" style="background:#fff;color:#111;max-width:420px;width:100%;border-radius:8px;padding:16px 18px;box-shadow:0 8px 32px rgba(0,0,0,.2);font:14px/1.45 system-ui,sans-serif;">
        <h2 id="mer-title" style="margin:0 0 8px;font-size:16px;">回報圖資錯誤</h2>
        <p style="margin:0 0 12px;font-size:12px;color:#555;">送出後進入人工審查佇列，<b>不會</b>立刻改地圖或解鎖高程權威表。</p>
        <form id="mer-form">
          <label style="display:block;margin-bottom:8px;font-size:12px;">類型
            <select name="layer" style="display:block;width:100%;margin-top:4px;padding:6px;">${layerOpts}</select>
          </label>
          <label style="display:block;margin-bottom:8px;font-size:12px;">類別
            <select name="category" style="display:block;width:100%;margin-top:4px;padding:6px;">${catOpts}</select>
          </label>
          <label style="display:block;margin-bottom:8px;font-size:12px;">建議高程
            <select name="suggested_elevation" style="display:block;width:100%;margin-top:4px;padding:6px;">${elevOpts}</select>
          </label>
          <label style="display:block;margin-bottom:8px;font-size:12px;">說明（選填）
            <textarea name="comment" rows="3" maxlength="500" style="display:block;width:100%;margin-top:4px;padding:6px;resize:vertical;" placeholder="請勿填寫身分證／電話等個資"></textarea>
          </label>
          <div style="font-size:11px;color:#666;margin-bottom:12px;">
            ${esc(opts.name || '')} · ${esc(opts.sys || '')} · ${esc(opts.feature_std_id || '')}<br/>
            現況高程：${esc(opts.current_elevation || '—')}
          </div>
          <div id="mer-msg" style="font-size:12px;min-height:1.2em;margin-bottom:8px;"></div>
          <div style="display:flex;gap:8px;justify-content:flex-end;">
            <button type="button" id="mer-cancel" style="padding:8px 12px;">取消</button>
            <button type="submit" id="mer-submit" style="padding:8px 12px;background:#1565C0;color:#fff;border:none;border-radius:4px;">送出</button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    overlay.querySelector('#mer-cancel').addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });

    overlay.querySelector('#mer-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;
        const msg = overlay.querySelector('#mer-msg');
        const btn = overlay.querySelector('#mer-submit');
        btn.disabled = true;
        msg.style.color = '#555';
        msg.textContent = '送出中…';

        const payload = {
            layer: form.layer.value,
            category: form.category.value,
            suggested_elevation: form.suggested_elevation.value || null,
            comment: form.comment.value,
            feature_std_id: opts.feature_std_id || '',
            sys: opts.sys || '',
            name: opts.name || '',
            current_elevation: opts.current_elevation || null,
            ...ctx,
            client_meta: {
                version: opts.appVersion || '',
                profile: ''
            }
        };

        try {
            const res = await fetch('/api/map-reports', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 401) {
                msg.style.color = '#c62828';
                msg.textContent = '請先登入後再回報。';
                btn.disabled = false;
                return;
            }
            if (res.status === 429) {
                msg.style.color = '#c62828';
                msg.textContent = data.message || '回報過於頻繁。';
                btn.disabled = false;
                return;
            }
            if (!res.ok) {
                msg.style.color = '#c62828';
                msg.textContent = data.message || data.error || '送出失敗';
                btn.disabled = false;
                return;
            }
            msg.style.color = '#2e7d32';
            msg.textContent = data.message || `已入佇列（${data.report_id}）`;
            setTimeout(close, 1200);
        } catch (err) {
            msg.style.color = '#c62828';
            msg.textContent = err.message || '網路錯誤';
            btn.disabled = false;
        }
    });
}

/**
 * popup 內嵌連結 HTML（呼叫端綁 click）
 */
export function mapErrorReportLinkHtml(attrs = {}) {
    const data = encodeURIComponent(JSON.stringify(attrs));
    return `<a href="#" class="map-error-report-link" data-mer="${data}" style="font-size:11px;color:#1565C0;">回報錯誤</a>`;
}

export function bindMapErrorReportLinks(root, { map, appVersion } = {}) {
    if (!root) return;
    root.querySelectorAll?.('.map-error-report-link').forEach((a) => {
        a.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            let attrs = {};
            try {
                attrs = JSON.parse(decodeURIComponent(a.getAttribute('data-mer') || '{}'));
            } catch {
                attrs = {};
            }
            openMapErrorReportForm({ ...attrs, map, appVersion });
        });
    });
}

export { LAYERS, CATEGORIES, ELEVATIONS };
